import { useQuery } from "@tanstack/react-query";
import { AnalyticsService } from "@gen/tracker/v1/analytics_connect";
import { SortOrder, DateRange, Pagination } from "@gen/tracker/v1/common_pb";
import { StatsSortOptions, type DrilldownRow } from "@gen/tracker/v1/analytics_pb";
import { DimensionFilter, MetricFilter, type StatsRow } from "@gen/tracker/v1/reporting_pb";

import { createClient } from "@/lib/connect";
import type { ConversionBasis } from "@/lib/period";
import type { ReportFilters } from "@/lib/report";
import { basisToProto } from "@/lib/services/analytics-service";

const client = createClient(AnalyticsService);

/** How many top-level groups one screen asks for. */
export const REPORT_PAGE = 300;

export interface ReportQuery {
  /**
   * The campaigns to read. Empty = every campaign the caller sees, archive
   * included; «Отчёты» passes the campaigns list's own ids, so its «Итого» is
   * the list's for the same period.
   */
  streamIds?: string[];
  from: string;
  to: string;
  timezone: string;
  /** Outermost first, 1..4. */
  dims: string[];
  basis: ConversionBasis;
  filters: ReportFilters;
  /** Only the parents' rows too (the tree). */
  subtotals: boolean;
  sort?: { key: string; desc: boolean };
  compare?: { from: string; to: string };
  limit?: number;
}

export interface ReportResult {
  rows: DrilldownRow[];
  /** Top-level groups under the filters (paging is over them). */
  total: number;
  totals?: StatsRow;
  compareTotals?: StatsRow;
}

/**
 * The rows on screen while the next query loads — only when they have the
 * shape the screen will read them with. A row's `dimension_path` is
 * positional: the table names level i by `dims[i]`. Switching «По
 * объявлениям» (campaign › creative › country) to «Ленды» (landing ›
 * campaign) under keepPreviousData drew three-level rows against two levels,
 * `dims[2]` was undefined and the screen fell over (У5x). Another period,
 * filter or sort keeps the old rows; other levels show the loading state. Pure.
 */
export function sameShapePlaceholder<T>(dims: readonly string[], prev: T | undefined, prevKey: readonly unknown[] | undefined): T | undefined {
  const prevDims = (prevKey?.[2] as Partial<ReportQuery> | undefined)?.dims;
  if (prev === undefined || !prevDims || prevDims.length !== dims.length) return undefined;
  return prevDims.every((d, i) => d === dims[i]) ? prev : undefined;
}

/**
 * One GetBreakdownDrilldown: the campaigns asked for (none = every campaign
 * the caller sees — the server has no ceiling), the filters in SQL, the
 * metric conditions as HAVING, every level counted by the server when
 * `subtotals`.
 */
export function useReport(q: ReportQuery, enabled = true) {
  return useQuery({
    queryKey: ["reports", "drilldown", q] as const,
    enabled: enabled && q.dims.length > 0,
    placeholderData: (prev, prevQuery) => sameShapePlaceholder(q.dims, prev, prevQuery?.queryKey),
    queryFn: async (): Promise<ReportResult> => {
      const res = await client.getBreakdownDrilldown({
        streamIds: q.streamIds ?? [],
        dateRange: new DateRange({ from: q.from, to: q.to, timezone: q.timezone }),
        dimensionKeys: q.dims,
        attributionBasis: basisToProto(q.basis),
        sort: q.sort ? new StatsSortOptions({ metricKey: q.sort.key, order: q.sort.desc ? SortOrder.DESC : SortOrder.ASC }) : undefined,
        pagination: new Pagination({ limit: q.limit ?? REPORT_PAGE, offset: 0 }),
        dimensionFilters: q.filters.dims.map((d) => new DimensionFilter({ dimensionKey: d.key, op: d.op, values: d.values })),
        metricFilters: q.filters.metrics.map((m) => new MetricFilter({ metricKey: m.key, op: m.op, value: m.value })),
        compareRange: q.compare ? new DateRange({ from: q.compare.from, to: q.compare.to, timezone: q.timezone }) : undefined,
        withSubtotals: q.subtotals,
      });
      return { rows: res.rows, total: res.pagination?.total ?? res.rows.length, totals: res.totals, compareTotals: res.compareTotals };
    },
  });
}
